import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import { Button, ControlGroup, Heading, majorScale } from '../../../npaper';
import { CurrencySelect } from '../../../npaper/CurrencySelect';
import { ICompany } from '../../../stores';
import { CompanySelect } from '../../companies/CompanySelect';

interface FormValues {
  supplier: ICompany;
  currency: string;
}

const PreImportForm = ({
  onContinue,
}: {
  onContinue: (supplier: ICompany, currency: string) => void;
}) => {
  const intl = useIntl();

  const { handleSubmit, control, errors, setValue } = useForm<FormValues>();

  const onSubmit = (values: FormValues) => {
    onContinue(values.supplier, values.currency);
  };

  return (
    <div className="py-8 w-full max-w-4xl mx-auto">
      <Heading size={300} marginBottom={majorScale(1)}>
        <FormattedMessage defaultMessage="Are you importing from a supplier?" />
      </Heading>
      <p className="mt-2 mb-2">
        <FormattedMessage defaultMessage="You can choose to import goods from a supplier. This helps you collect all the products from your suppliers into a single source with imported stock statuses." />
      </p>
      <div className="max-w-sm">
        <Controller
          control={control}
          name="supplier"
          render={({ onChange, value }) => (
            <ControlGroup>
              <CompanySelect
                selected={value ? value.id : null}
                onSelect={(supplier) => {
                  onChange(supplier);
                  setValue('currency', supplier.currency);
                }}
                placeholder={intl.formatMessage({
                  defaultMessage: 'Select supplier or leave empty',
                })}
              />
              {value && (
                <Button iconLeft="cross" onClick={() => onChange(null)} />
              )}
            </ControlGroup>
          )}
        />
      </div>

      <Heading
        size={300}
        marginTop={majorScale(4)}
        marginBottom={majorScale(1)}
      >
        <FormattedMessage defaultMessage="Choose an import Currency" />
      </Heading>
      <Box is="p" className="mt-2 mb-2">
        <FormattedMessage defaultMessage="You must select the currency that your input data has." />
      </Box>
      <div className="max-w-sm">
        <Controller
          name="currency"
          control={control}
          rules={{
            required: intl.formatMessage({
              defaultMessage: 'Please select a currency to use for the import',
            }),
          }}
          render={({ onChange, value }) => (
            <CurrencySelect onChange={onChange} value={value} />
          )}
        />
        {errors && errors.currency && (
          <p className="small danger">{errors.currency.message}</p>
        )}
      </div>

      <div className="mt-8 flex">
        <Button
          primary
          iconRight="arrow-right"
          onClick={handleSubmit(onSubmit)}
        >
          <FormattedMessage defaultMessage="Continue" />
        </Button>
      </div>
    </div>
  );
};

export default PreImportForm;
